import type { SailPackage, SailRef } from '@/root/shared-types'
import { SAIL_STATUS } from '@/root/shared-consts'

/**
 * Cancellation state of a single reservation leg (outbound or return).
 *
 * - `isRefundedByCustomer` — customer self-cancelled via refund (sailPackage.refundOfferId).
 * - `isCancelledByAdmin`   — admin cancelled the sail (sailRef.status === 'CANCELLED').
 * - `isExpired`            — cancellation grace period has passed (sailRef.expired when cancelled).
 * - `isEditDisallowed`     — backend explicitly forbids editing this leg (sailRef.allowance.edit === false).
 */
export interface LegCancellationState {
  isRefundedByCustomer: boolean
  isCancelledByAdmin: boolean
  isExpired: boolean
  isEditDisallowed: boolean
}

/** Leg should be completely hidden from the edit UI (one-way mode). */
export const isLegHidden = (state: LegCancellationState): boolean =>
  state.isRefundedByCustomer || (state.isCancelledByAdmin && state.isExpired) || state.isEditDisallowed

/** Leg is admin-cancelled but still editable (user can pick a new date to "recover" it). */
export const isLegAdminCancelledEditable = (state: LegCancellationState): boolean =>
  state.isCancelledByAdmin && !state.isExpired && !state.isRefundedByCustomer

export const getLegCancellationState = (
  sailPackage: SailPackage | undefined,
  sailRef: SailRef | undefined,
): LegCancellationState => ({
  isRefundedByCustomer: Boolean(sailPackage?.refundOfferId),
  isCancelledByAdmin: sailRef?.status === SAIL_STATUS.CANCELLED,
  isExpired: sailRef?.status === SAIL_STATUS.CANCELLED && Boolean(sailRef.expired),
  isEditDisallowed: sailRef?.allowance?.edit === false,
})
